import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertCircle, Pencil, Trash2, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [staleTools, setStaleTools] = useState([]);
  const [weights, setWeights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTool, setEditingTool] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      toast.error('Please login first');
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [toolsRes, staleRes, weightsRes] = await Promise.all([
        axios.get(`${API}/tools`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/stale-tools`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/ranking-weights`)
      ]);

      setTools(toolsRes.data);
      setStaleTools(staleRes.data.stale_tools || []);
      setWeights(weightsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTool = async (toolId) => {
    if (!window.confirm('Are you sure you want to delete this tool?')) return;

    try {
      await axios.delete(`${API}/tools/${toolId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tool deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting tool:', error);
      toast.error('Failed to delete tool');
    }
  };

  const handleUpdateWeights = async (e) => {
    e.preventDefault();
    
    const total = parseFloat(weights.capability_match) + 
                  parseFloat(weights.constraint_match) + 
                  parseFloat(weights.tag_alignment) + 
                  parseFloat(weights.recency);
    
    if (Math.abs(total - 100) > 0.1) {
      toast.error('Weights must sum to 100%');
      return;
    }

    try {
      await axios.put(`${API}/ranking-weights`, weights, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ranking weights updated successfully');
      fetchData();
    } catch (error) {
      console.error('Error updating weights:', error);
      toast.error('Failed to update weights');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <Layout>
        <div className="py-20 text-center" data-testid="loading-state">
          <p className="text-xl text-[#333333]">Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex items-center justify-between mb-12">
            <h1 className="text-4xl md:text-5xl font-semibold text-black" data-testid="admin-dashboard-title">
              Admin Dashboard
            </h1>
            <Button
              onClick={handleLogout}
              className="bg-transparent border-2 border-[#0044CC] text-[#0044CC] hover:bg-[#0044CC]/5 h-12 px-8 rounded-full font-semibold text-lg"
              data-testid="logout-button"
            >
              Logout
            </Button>
          </div>

          <Tabs defaultValue="tools" className="w-full">
            <TabsList className="mb-8 bg-gray-100 p-1 rounded-xl inline-flex">
              <TabsTrigger 
                value="tools" 
                className="px-6 py-3 rounded-lg data-[state=active]:bg-white data-[state=active]:text-black text-[#333333]"
                data-testid="tools-tab"
              >
                All Tools ({tools.length})
              </TabsTrigger>
              <TabsTrigger 
                value="stale" 
                className="px-6 py-3 rounded-lg data-[state=active]:bg-white data-[state=active]:text-black text-[#333333]"
                data-testid="stale-tab"
              >
                Stale Data ({staleTools.length})
              </TabsTrigger>
              <TabsTrigger 
                value="weights" 
                className="px-6 py-3 rounded-lg data-[state=active]:bg-white data-[state=active]:text-black text-[#333333]"
                data-testid="weights-tab"
              >
                Ranking Weights
              </TabsTrigger>
            </TabsList>

            <TabsContent value="tools">
              <div className="mb-6">
                <Button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-12 px-8 rounded-full font-semibold text-lg inline-flex items-center gap-2"
                  data-testid="add-tool-button"
                >
                  <Plus className="h-5 w-5" />
                  Add New Tool
                </Button>
              </div>

              {showAddForm && (
                <div className="mb-8 bg-gray-50 border-2 border-gray-200 p-8 rounded-2xl">
                  <h2 className="text-2xl font-semibold text-black mb-6">Add New Tool</h2>
                  <p className="text-base text-[#333333] mb-4">
                    Note: For MVP, please use the database directly to add tools with complete data. This form is for future implementation.
                  </p>
                  <Button
                    onClick={() => setShowAddForm(false)}
                    className="bg-transparent border-2 border-gray-300 text-black hover:bg-gray-100 h-12 px-6 rounded-full"
                  >
                    Close
                  </Button>
                </div>
              )}

              <div className="space-y-4">
                {tools.map((tool, index) => (
                  <div
                    key={tool.id}
                    className="bg-white border-2 border-gray-100 p-6 rounded-2xl"
                    data-testid={`tool-item-${index}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-2xl font-semibold text-black">{tool.name}</h3>
                          <span className="px-3 py-1 bg-gray-100 text-black text-sm font-medium rounded-full">
                            {tool.primary_category}
                          </span>
                          {tool.data_staleness_flag && (
                            <span className="px-3 py-1 bg-yellow-100 text-black text-sm font-medium rounded-full">
                              Stale
                            </span>
                          )}
                        </div>
                        <p className="text-base text-[#333333] mb-3">{tool.description_plain}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-[#333333]">
                          <div><strong className="text-black">Pricing:</strong> {tool.pricing_model}</div>
                          <div><strong className="text-black">Skill Level:</strong> {tool.skill_level}</div>
                          <div><strong className="text-black">Last Updated:</strong> {new Date(tool.last_updated).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div className="flex gap-3 ml-4">
                        <Button
                          onClick={() => window.open(tool.official_website, '_blank')}
                          className="bg-transparent border-2 border-[#0044CC] text-[#0044CC] hover:bg-[#0044CC]/5 h-10 px-4 rounded-full"
                          data-testid={`view-website-${index}`}
                        >
                          Visit
                        </Button>
                        <Button
                          onClick={() => handleDeleteTool(tool.id)}
                          className="bg-transparent border-2 border-[#D92D20] text-[#D92D20] hover:bg-[#D92D20]/5 h-10 w-10 rounded-full p-0"
                          data-testid={`delete-tool-${index}`}
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="stale">
              {staleTools.length === 0 ? (
                <div className="text-center py-12 bg-white border-2 border-gray-100 rounded-2xl">
                  <p className="text-xl text-[#333333]">No stale tools found. All data is up to date!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {staleTools.map((tool, index) => (
                    <div
                      key={tool.id}
                      className="bg-yellow-50 border-2 border-yellow-200 p-6 rounded-2xl"
                      data-testid={`stale-tool-${index}`}
                    >
                      <div className="flex items-start gap-4">
                        <AlertCircle className="h-6 w-6 text-black flex-shrink-0 mt-1" />
                        <div className="flex-grow">
                          <h3 className="text-2xl font-semibold text-black mb-2">{tool.name}</h3>
                          <p className="text-base text-[#333333] mb-3">{tool.staleness_reason}</p>
                          <div className="flex gap-3">
                            <Button
                              onClick={() => window.open(tool.official_website, '_blank')}
                              className="bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-10 px-6 rounded-full"
                            >
                              Visit Website
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="weights">
              {weights && (
                <div className="bg-white border-2 border-gray-100 p-8 rounded-2xl max-w-2xl">
                  <h2 className="text-3xl font-semibold text-black mb-6">
                    Configure Ranking Weights
                  </h2>
                  <p className="text-base md:text-lg text-[#333333] mb-8">
                    Adjust how much each factor contributes to tool rankings. Total must equal 100%.
                  </p>

                  <form onSubmit={handleUpdateWeights} className="space-y-6">
                    <div>
                      <Label className="text-lg font-semibold text-black mb-2 block">
                        Capability Match: {weights.capability_match}%
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={weights.capability_match}
                        onChange={(e) => setWeights({ ...weights, capability_match: parseFloat(e.target.value) })}
                        className="h-12 px-4 bg-gray-50 border-2 border-transparent focus:border-[#0044CC] rounded-xl"
                        data-testid="capability-weight-input"
                      />
                    </div>

                    <div>
                      <Label className="text-lg font-semibold text-black mb-2 block">
                        Constraint Match: {weights.constraint_match}%
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={weights.constraint_match}
                        onChange={(e) => setWeights({ ...weights, constraint_match: parseFloat(e.target.value) })}
                        className="h-12 px-4 bg-gray-50 border-2 border-transparent focus:border-[#0044CC] rounded-xl"
                        data-testid="constraint-weight-input"
                      />
                    </div>

                    <div>
                      <Label className="text-lg font-semibold text-black mb-2 block">
                        Tag Alignment: {weights.tag_alignment}%
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={weights.tag_alignment}
                        onChange={(e) => setWeights({ ...weights, tag_alignment: parseFloat(e.target.value) })}
                        className="h-12 px-4 bg-gray-50 border-2 border-transparent focus:border-[#0044CC] rounded-xl"
                        data-testid="tag-weight-input"
                      />
                    </div>

                    <div>
                      <Label className="text-lg font-semibold text-black mb-2 block">
                        Recency: {weights.recency}%
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={weights.recency}
                        onChange={(e) => setWeights({ ...weights, recency: parseFloat(e.target.value) })}
                        className="h-12 px-4 bg-gray-50 border-2 border-transparent focus:border-[#0044CC] rounded-xl"
                        data-testid="recency-weight-input"
                      />
                    </div>

                    <div className="pt-4 border-t-2 border-gray-100">
                      <p className="text-lg font-semibold text-black mb-4">
                        Total: {(weights.capability_match + weights.constraint_match + weights.tag_alignment + weights.recency).toFixed(1)}%
                      </p>
                      <Button
                        type="submit"
                        className="bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-12 px-8 rounded-full font-semibold text-lg"
                        data-testid="update-weights-button"
                      >
                        Update Weights
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboardPage;
